import React from 'react';
import { useTranslation } from 'react-i18next';
import { CornerDownLeft, Loader2, Search, Sparkles, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface AiSearchState {
    /** Uuids the model chose, in its order. Null means no AI search is active. */
    uuids: string[] | null;
    /** What it took the request to mean, shown back so a wrong read is visible. */
    understood: string;
    /** Set when it could not run — no credits, nothing described yet. */
    unavailable?: string;
    loading: boolean;
}

/**
 * The catalogue's search box, with a switch for how hard to look.
 *
 * Both modes search on Enter. The catalogue is queried on the server now, so a
 * search per keystroke would be a request per keystroke — and the AI mode costs
 * a credit or two a call. The return-key hint shows whenever what is typed has
 * not been searched yet, so it is always clear that Enter is what runs it.
 *
 * Off, the server matches every word against names, SKUs and collections, and
 * any colour or material word against what the product looks like. On, the
 * phrase goes to a model that reads the one-line description of each product —
 * the only way to answer "design of a dragon".
 */
export const CatalogSearch: React.FC<{
    value: string;
    onChange: (next: string) => void;
    /** The text the list currently reflects. */
    submitted: string;
    onSubmit: (text: string) => void;
    /** A plain search is running. */
    searching?: boolean;
    aiEnabled: boolean;
    onAiEnabledChange: (next: boolean) => void;
    ai: AiSearchState;
    onRunAi: () => void;
    onClearAi: () => void;
}> = ({ value, onChange, submitted, onSubmit, searching, aiEnabled, onAiEnabledChange, ai, onRunAi, onClearAi }) => {
    const { t } = useTranslation();

    const pending = value.trim() !== submitted.trim();
    const busy = searching || ai.loading;

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (aiEnabled) {
            if (value.trim()) onRunAi();
            return;
        }
        onSubmit(value.trim());
    };

    const clear = () => {
        onChange('');
        if (aiEnabled) onClearAi();
        if (submitted) onSubmit('');
    };

    return (
        <div className="flex w-full flex-col gap-1.5 sm:w-auto">
            <form onSubmit={submit} className="relative w-full sm:w-96">
                <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && value) {
                            e.preventDefault();
                            clear();
                        }
                    }}
                    placeholder={
                        aiEnabled
                            ? (t('PAGES.MY_PRODUCTS.SEARCH_AI_PLACEHOLDER') as string)
                            : (t('PAGES.MY_PRODUCTS.SEARCH_PLACEHOLDER') as string)
                    }
                    aria-describedby="catalog-search-hint"
                    // Room on the end for clear, the Enter hint and the AI toggle.
                    className="h-9 ps-8 pe-40"
                />

                <div className="absolute end-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
                    {busy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}

                    {/* Only while Enter would do something: an Enter hint on an
                        empty or already-searched box is an instruction to nowhere. */}
                    {!busy && pending && value.trim() && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="submit"
                                    aria-label={t('PAGES.MY_PRODUCTS.SEARCH_PRESS_ENTER') as string}
                                    className="flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    <CornerDownLeft className="size-3" />
                                    <span className="hidden sm:inline">{t('PAGES.MY_PRODUCTS.SEARCH_ENTER_KEY')}</span>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">{t('PAGES.MY_PRODUCTS.SEARCH_PRESS_ENTER')}</TooltipContent>
                        </Tooltip>
                    )}

                    {value && (
                        <button
                            type="button"
                            onClick={clear}
                            aria-label={t('PAGES.MY_PRODUCTS.SEARCH_CLEAR') as string}
                            className="grid size-6 place-items-center text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3.5" />
                        </button>
                    )}

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                size="sm"
                                variant={aiEnabled ? 'default' : 'ghost'}
                                aria-pressed={aiEnabled}
                                className="h-7 gap-1 px-2 text-xs"
                                onClick={() => {
                                    const next = !aiEnabled;
                                    onAiEnabledChange(next);
                                    // Turning it off drops its results, or the
                                    // list would keep showing what the model
                                    // chose while the switch says otherwise.
                                    if (!next) onClearAi();
                                }}
                            >
                                <Sparkles className="size-3.5" />
                                {t('PAGES.MY_PRODUCTS.USE_AI')}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-64">
                            {t('PAGES.MY_PRODUCTS.USE_AI_HINT')}
                        </TooltipContent>
                    </Tooltip>
                </div>
            </form>

            <span id="catalog-search-hint" className="sr-only">
                {t('PAGES.MY_PRODUCTS.SEARCH_PRESS_ENTER')}
            </span>

            {/*
              What it understood, always shown when it ran.

              A search that silently returns four products leaves the vendor
              unable to tell a good match from a misreading. Saying "looking for:
              dragon motifs" makes a wrong interpretation obvious at a glance,
              and an empty result honest rather than broken.
            */}
            {ai.uuids !== null && !ai.loading && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className={cn(ai.uuids.length === 0 ? 'text-muted-foreground' : 'text-foreground')}>
                        {ai.uuids.length === 0
                            ? t('PAGES.MY_PRODUCTS.AI_NONE', { query: ai.understood || value })
                            : t('PAGES.MY_PRODUCTS.AI_FOUND', { count: ai.uuids.length, query: ai.understood || value })}
                    </span>
                    <button
                        type="button"
                        onClick={onClearAi}
                        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                    >
                        <X className="size-3" />
                        {t('PAGES.MY_PRODUCTS.AI_CLEAR')}
                    </button>
                </div>
            )}

            {ai.unavailable && !ai.loading && (
                <p className="text-xs text-destructive">{ai.unavailable}</p>
            )}
        </div>
    );
};
